// WAAP Purge — one-shot cache purge.
// PUBLIC Open-API surface (root conversant SDK, HMAC). The private surface had a
// single POST /purge that branched on `mode`; the public surface splits
// it into three dedicated routes, so the hook dispatches on `mode`:
//   POST /waap/domains/:domainId/purge/url    { urls: [] }        (mode: url)
//   POST /waap/domains/:domainId/purge/all     (no body)          (mode: everything)
//   POST /waap/domains/:domainId/purge/custom { operator, pattern } (mode: custom)
// :domainId anchors the website; the BE resolves the vendor service from it.
// Each route returns an un-enveloped batch id: { batch_id: number }.
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** Discriminated purge request — `mode` selects which public route is called. */
export interface WaapPurgeRequest {
  mode: 'url' | 'custom' | 'everything';
  urls?: string[];
  operator?: string; // startswith | endswith
  pattern?: string;
}

/** Vendor batch id returned by every public purge op. */
export interface WaapPurgeBatch {
  batch_id: number;
}

const purgePath = (domainId: string, op: 'url' | 'all' | 'custom') =>
  `/waap/domains/${domainId}/purge/${op}`;

/** Trigger a cache purge (fire-and-forget; nothing to invalidate). */
export function useWaapPurge(
  domainId: string,
  options?: UseMutationOptions<WaapPurgeBatch, unknown, WaapPurgeRequest>,
) {
  return useMutation<WaapPurgeBatch, unknown, WaapPurgeRequest>({
    mutationFn: async (req: WaapPurgeRequest) => {
      if (req.mode === 'url') {
        const res = await serviceApiV3.post<WaapPurgeBatch>(purgePath(domainId, 'url'), {
          urls: req.urls ?? [],
        });
        return res.data;
      }
      if (req.mode === 'custom') {
        const res = await serviceApiV3.post<WaapPurgeBatch>(purgePath(domainId, 'custom'), {
          operator: req.operator ?? '',
          pattern: req.pattern ?? '',
        });
        return res.data;
      }
      // mode === 'everything' → purge all (no body).
      const res = await serviceApiV3.post<WaapPurgeBatch>(purgePath(domainId, 'all'));
      return res.data;
    },
    ...options,
  });
}
